/**
 * Loadout / inventory panel — docs/04 §7, row for row.
 *
 * The governing constraint: *difficulty comes from the world and decisions, not
 * from inventory friction.* Every affordance here exists to make a build change
 * cheap, so every primary action is a single click on a row that is already on
 * screen — no drag, no confirm, no modal chain. The P7 gate is "loadout change
 * < 4 clicks" (docs/05 §6) and the layout is designed backwards from it:
 *
 *   equip from stash   1 click   [MAIN] / [OFF] on the row
 *   install a module   1 click   [FIT] installs into the focus weapon
 *   save a loadout     1 click   [SAVE] on a named slot
 *   restore a loadout  1 click   [LOAD] on a named slot
 *
 * Removal never consumes a module (docs/04 §7, last line) — respec is free and
 * the UI says so out loud, because a buildcraft game whose players stop
 * experimenting has failed.
 *
 * Rendering is rebuild-on-dirty, not per-frame: `update()` returns immediately
 * unless the inventory revision or some local view state actually moved.
 */

import { btn, clear, el } from './style';
import {
  buildComparison,
  buildModuleComparison,
  byFavouriteThenRarity,
  computeSynergy,
  formatStatValue,
  keyOf,
  moduleFitsWeapon,
  moduleSearchBlob,
  moduleStatIsGood,
  moduleStatText,
  SLOT_ORDER,
  targetSlotFor,
  weaponSearchBlob,
  type CompareRowVM,
  type CraftArgsVM,
  type GameVM,
  type Hand,
  type InventoryVM,
  type ModuleVM,
  type RecipeVM,
  type SlotKind,
  type SynergyHintVM,
  type UICallbacks,
  type WeaponVM,
} from './viewModels';

type Tab = 'weapons' | 'modules' | 'loadouts' | 'craft';

const TABS: readonly Tab[] = ['weapons', 'modules', 'loadouts', 'craft'];

const MAX_COMPARE = 3;

/**
 * Six dropdowns and two checkboxes sat above a list whose real contents are the
 * two or three things the run is carrying — a filter bar wider than the data it
 * filters. What survived is the pair that earns its space: free text (the one
 * filter that scales with the catalogue behind the run) and slot compatibility
 * (the only filter that answers a question the player actually has).
 */
interface Filters {
  q: string;
  /** Only show modules that physically fit the focus weapon. */
  fitOnly: boolean;
}

export class LoadoutPanel {
  readonly root: HTMLElement;

  private cb: UICallbacks;
  private inv: InventoryVM | null = null;
  private rev = -1;

  private tab: Tab = 'weapons';
  private filters: Filters = { q: '', fitOnly: true };

  /** Install target and default synergy subject. Defaults to the main hand. */
  private focusId: string | null = null;
  /** Transient hover/selection — what the detail pane is describing. */
  private inspectId: string | null = null;
  private compare: string[] = [];
  /**
   * Which kind the tray is holding. Weapons and modules have disjoint stat
   * vocabularies, so a mixed tray would be a table of blanks; adding the other
   * kind swaps the tray rather than refusing the click.
   */
  private compareKind: 'weapon' | 'module' = 'weapon';
  private cursor = 0;
  private visibleIds: string[] = [];

  /**
   * The workbench's two material pickers. Held on the panel rather than in the
   * view model because they are a *selection*, not state the game owns — the
   * same reason `focusId` lives here.
   */
  private craftFrom = '';
  private craftTo = '';
  /**
   * Materials the codex has recorded, as the pickers' options. A conversion
   * target you have never seen is a target you cannot reason about, so the
   * offer is exactly the discovered set — the same gate meta puts on which
   * recipes are listed at all.
   */
  private materials: Array<{ id: string; label: string }> = [];

  private listDirty = true;
  private detailDirty = true;
  private sideDirty = true;
  private cmpDirty = true;

  // --- element handles
  private tabBtns: Record<Tab, HTMLButtonElement>;
  private search: HTMLInputElement;
  private cbFit: HTMLInputElement;
  private listHost: HTMLElement;
  private sideHost: HTMLElement;
  private buildHead: HTMLElement;
  private detailHost: HTMLElement;
  private cmpHost: HTMLElement;
  private countText: HTMLElement;
  private restText: HTMLElement;

  constructor(cb: UICallbacks) {
    this.cb = cb;
    this.root = el('div', 'panel');
    this.root.tabIndex = -1;
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-label', 'Loadout');

    // ---------------------------------------------------------- header
    const head = el('div', 'panel__head');
    head.append(el('div', 'panel__title', 'Loadout'));

    const tabs = el('div', 'tabs');
    const mk = (t: Tab, label: string): HTMLButtonElement => {
      const b = btn(label, 'tab');
      b.dataset.tab = t;
      tabs.appendChild(b);
      return b;
    };
    this.tabBtns = {
      weapons: mk('weapons', 'Weapons'),
      modules: mk('modules', 'Modules'),
      loadouts: mk('loadouts', 'Loadouts'),
      // The workbench is a rest-node service like the stash, so it lives beside
      // the other rest-node service rather than in a panel of its own.
      craft: mk('craft', 'Craft'),
    };
    head.append(tabs);

    this.countText = el('div', 'ui-mute ui-grow');
    this.restText = el('div', 'ui-dim');
    head.append(this.countText, this.restText, btn('✕', 'close'));

    // ---------------------------------------------------------- filters
    const filters = el('div', 'filters');
    this.search = document.createElement('input');
    this.search.type = 'search';
    this.search.placeholder = 'search name · family · tag · effect…';
    this.search.setAttribute('aria-label', 'Search inventory');

    const fitLabel = el('label');
    this.cbFit = document.createElement('input');
    this.cbFit.type = 'checkbox';
    this.cbFit.checked = true;
    fitLabel.append(this.cbFit, document.createTextNode('fits focus'));
    fitLabel.title = 'Slot compatibility: hide modules the focus weapon has no slot for';

    filters.append(this.search, fitLabel, btn('reset', 'reset-filters'));

    // ---------------------------------------------------------- body
    //
    // Browse on the left, *build* in the middle, argument on the right. The
    // hands and their sockets used to be a 250px side rail while the middle of
    // the screen went to a list of things you were not holding — which put the
    // one thing every action in this panel targets furthest from the eye, and
    // left the module tab with no reminder of what [FIT] would hit.
    const body = el('div', 'panel__body');

    const left = el('div', 'col col--list');
    this.listHost = el('div', 'scroll');
    this.listHost.setAttribute('role', 'listbox');
    left.append(this.listHost);

    const centre = el('div', 'col ui-grow');
    this.buildHead = el('div', 'col__head', 'Build');
    this.sideHost = el('div', 'scroll build');
    centre.append(this.buildHead, this.sideHost);

    const right = el('div', 'col col--right');
    right.append(el('div', 'col__head', 'Detail · synergy'));
    this.detailHost = el('div', 'scroll detail');
    right.append(this.detailHost);

    body.append(left, centre, right);

    // ---------------------------------------------------------- compare tray
    this.cmpHost = el('div', 'cmp');

    // ---------------------------------------------------------- footer
    const foot = el('div', 'panel__foot');
    // `f` sets the focus weapon, which is what every [FIT] in the panel lands
    // on — it was the one key the footer did not teach.
    foot.innerHTML =
      '<span><kbd>↑↓</kbd> move</span><span><kbd>↵</kbd> equip / fit</span>' +
      '<span><kbd>⇧↵</kbd> off-hand</span><span><kbd>space</kbd> ★</span>' +
      '<span><kbd>f</kbd> focus</span><span><kbd>c</kbd> compare</span>' +
      '<span><kbd>/</kbd> search</span>' +
      '<span><kbd>1-4</kbd> tab</span><span><kbd>esc</kbd> close</span>';
    const freebie = el(
      'span',
      'ui-good',
      'Respec is free — removing a module never consumes it.',
    );
    foot.append(freebie);

    this.root.append(head, filters, body, this.cmpHost, foot);
    this.wire();
  }

  // ------------------------------------------------------------- wiring

  private wire(): void {
    // One delegated click listener. 40 weapons × 4 buttons is 160 listeners
    // otherwise, re-attached on every rebuild.
    this.root.addEventListener('click', (e) => this.onClick(e));
    this.root.addEventListener('mouseover', (e) => {
      const row = (e.target as HTMLElement).closest<HTMLElement>('[data-id]');
      if (!row || !row.dataset.id) return;
      if (this.inspectId === row.dataset.id) return;
      this.inspectId = row.dataset.id;
      this.detailDirty = true;
      this.render();
    });

    this.search.addEventListener('input', () => {
      this.filters.q = this.search.value.trim().toLowerCase();
      this.listDirty = true;
      this.render();
    });

    this.cbFit.addEventListener('change', () => {
      this.filters.fitOnly = this.cbFit.checked;
      this.listDirty = true;
      this.render();
    });

    // The workbench's material pickers. Delegated for the same reason the
    // clicks are: the list is rebuilt on every dirty pass and per-element
    // listeners would be re-attached with it.
    this.listHost.addEventListener('change', (e) => {
      const sel = (e.target as HTMLElement).closest<HTMLSelectElement>('select[data-craft]');
      if (!sel) return;
      if (sel.dataset.craft === 'from') this.craftFrom = sel.value;
      else this.craftTo = sel.value;
      this.listDirty = true;
      this.render();
    });
  }

  private onClick(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    const actEl = target.closest<HTMLElement>('[data-act]');
    const act = actEl?.dataset.act;
    const idEl = target.closest<HTMLElement>('[data-id]');
    const id = idEl?.dataset.id ?? null;

    if (act === 'close') {
      this.cb.onScreenChange?.('none');
      return;
    }
    if (act === 'tab') {
      this.setTab(actEl!.dataset.tab as Tab);
      return;
    }
    if (act === 'reset-filters') {
      this.resetFilters();
      return;
    }

    switch (act) {
      case 'equip-main':
        if (id) this.cb.onEquip?.(id, 'main');
        return;
      case 'equip-off':
        if (id) this.cb.onEquip?.(id, 'off');
        return;
      case 'unequip':
        this.cb.onUnequip?.(actEl!.dataset.hand as Hand);
        return;
      case 'stash':
        if (id) this.cb.onStash?.(id);
        return;
      // Keyed by `StashItemVM.id`, not by a weapon id: the stash holds
      // definitions with their own stable ids, and two stashed copies of one
      // weapon are two rows that must not both name the same withdrawal.
      case 'unstash':
        if (actEl!.dataset.stash) this.cb.onUnstash?.(actEl!.dataset.stash);
        return;
      case 'craft': {
        const recipe = this.inv?.crafting.recipes.find((x) => x.id === actEl!.dataset.id);
        if (!recipe) return;
        // The arguments are recomputed at click time rather than captured when
        // the row was drawn: the focus weapon and the pickers can both move
        // between the two, and crafting with a stale target is unrecoverable.
        const a = this.craftArgs(recipe);
        if (!a.ok) return;
        this.cb.onCraft?.(recipe.id, a.args);
        return;
      }
      case 'fav-weapon':
        if (id) this.cb.onToggleFavourite?.('weapon', id);
        return;
      case 'fav-module':
        if (id) this.cb.onToggleFavourite?.('module', id);
        return;
      case 'compare':
        if (id) this.toggleCompare(id);
        return;
      case 'compare-clear':
        this.compare.length = 0;
        this.cmpDirty = true;
        this.paintCompareButtons();
        this.render();
        return;
      case 'install':
        if (id) this.install(id);
        return;
      case 'remove-module': {
        const w = actEl!.dataset.weapon!;
        const kind = actEl!.dataset.slot as SlotKind;
        const idx = Number(actEl!.dataset.index);
        this.cb.onRemoveModule?.(w, kind, idx);
        return;
      }
      case 'focus':
        if (id) this.setFocus(id);
        return;
      case 'save-loadout': {
        const i = Number(actEl!.dataset.index);
        const input = this.root.querySelector<HTMLInputElement>(`input[data-lname="${i}"]`);
        this.cb.onSaveLoadout?.(i, input?.value || 'Loadout ' + (i + 1));
        return;
      }
      case 'load-loadout':
        this.cb.onRestoreLoadout?.(Number(actEl!.dataset.index));
        return;
      case 'clear-loadout':
        this.cb.onClearLoadout?.(Number(actEl!.dataset.index));
        return;
      case 'pair':
        this.cb.onSelectPair?.(Number(actEl!.dataset.index));
        return;
      case 'save-pair':
        this.cb.onSavePair?.(Number(actEl!.dataset.index));
        return;
      case 'codex-ref': {
        const kind = actEl!.dataset.kind as 'material' | 'reaction' | 'enemy';
        this.cb.onCodexOpenRef?.({ kind, id: actEl!.dataset.ref!, label: actEl!.textContent ?? '' });
        return;
      }
    }

    // Bare row click = focus it (module install target / synergy subject).
    if (id && idEl!.classList.contains('row')) this.setFocus(id);
  }

  // ------------------------------------------------------------- actions

  private setTab(t: Tab): void {
    if (this.tab === t) return;
    this.tab = t;
    this.cursor = 0;
    this.listDirty = true;
    this.detailDirty = true;
    this.render();
  }

  private resetFilters(): void {
    this.filters.q = '';
    this.filters.fitOnly = true;
    this.search.value = '';
    this.cbFit.checked = true;
    this.listDirty = true;
    this.render();
  }

  private setFocus(id: string): void {
    const inv = this.inv;
    if (!inv) return;
    // Only weapons can be the install target; clicking a module row just
    // inspects it.
    if (!inv.weapons.some((w) => keyOf(w) === id)) {
      this.inspectId = id;
      this.detailDirty = true;
      this.render();
      return;
    }
    this.focusId = id;
    this.inspectId = id;
    this.detailDirty = true;
    this.sideDirty = true;
    this.listDirty = true;
    this.render();
  }

  /**
   * Add or drop a comparison column. Reachable from module rows as well as
   * weapon rows: choosing a module is the buildcraft decision — it is made
   * dozens of times a run and it is free to undo — and it had no comparison
   * surface at all while the once-a-run weapon choice had the whole tray.
   */
  private toggleCompare(id: string): void {
    const inv = this.inv;
    const kind: 'weapon' | 'module' =
      inv && inv.modules.some((m) => m.id === id) ? 'module' : 'weapon';
    if (kind !== this.compareKind) {
      this.compareKind = kind;
      this.compare.length = 0;
    }
    const i = this.compare.indexOf(id);
    if (i >= 0) this.compare.splice(i, 1);
    else {
      // Up to 3 (docs/04 §7). Oldest falls off rather than refusing the click —
      // a refused click is friction, and friction is the thing being removed.
      if (this.compare.length >= MAX_COMPARE) this.compare.shift();
      this.compare.push(id);
    }
    this.cmpDirty = true;
    // Repaint just the CMP buttons. Rebuilding the list would throw away the
    // scroll position and the row you were about to click next.
    this.paintCompareButtons();
    this.render();
  }

  private paintCompareButtons(): void {
    for (const b of this.listHost.querySelectorAll<HTMLElement>('[data-act="compare"]')) {
      const id = b.closest<HTMLElement>('[data-id]')?.dataset.id;
      const on = !!id && this.compare.includes(id) && this.compareKind === kindOfTab(this.tab);
      if ((b.dataset.on === '1') !== on) b.dataset.on = on ? '1' : '0';
    }
  }

  /** Install into the focus weapon's first free slot of the module's kind. */
  private install(moduleId: string): void {
    const inv = this.inv;
    if (!inv) return;
    const m = inv.modules.find((x) => x.id === moduleId);
    const w = this.focusWeapon();
    if (!m || !w) return;
    const slot = targetSlotFor(m, w);
    if (!slot) return;
    this.cb.onInstallModule?.(keyOf(w), slot.kind, slot.index, m.id);
  }

  private focusWeapon(): WeaponVM | null {
    const inv = this.inv;
    if (!inv) return null;
    const id = this.focusId ?? inv.hands.main ?? inv.slots.find((s) => s) ?? null;
    return inv.weapons.find((w) => keyOf(w) === id) ?? null;
  }

  // ------------------------------------------------------------- lifecycle

  /** Called when the panel becomes visible. */
  onShow(): void {
    this.listDirty = this.detailDirty = this.sideDirty = this.cmpDirty = true;
    this.render();
    // Focus the panel, not the search box: arrow keys should drive the list by
    // default and `/` is one keystroke away when you actually want to type.
    this.root.focus();
  }

  update(vm: GameVM): void {
    const inv = vm.inventory;
    this.inv = inv;
    if (inv.revision !== this.rev) {
      this.rev = inv.revision;
      this.listDirty = this.detailDirty = this.sideDirty = this.cmpDirty = true;
    }
    // Counting is enough: discovery only ever adds, so a length that moved is a
    // recorded material that was not there before. This runs every frame the
    // panel is open and must not walk the table.
    if (vm.codex.materials.length > 0) {
      let found = 0;
      for (const m of vm.codex.materials) if (m.discovered) found++;
      if (found !== this.materials.length) {
        this.materials = vm.codex.materials
          .filter((m) => m.discovered)
          .map((m) => ({ id: m.id, label: m.name }));
        this.listDirty = true;
      }
    }
    if (this.focusId === null) this.focusId = inv.hands.main ?? null;
    this.render();
  }

  private render(): void {
    const inv = this.inv;
    if (!inv) return;
    for (const t of TABS) {
      this.tabBtns[t].dataset.on = this.tab === t ? '1' : '0';
    }
    // The header states the store's size, not just its door: "stash locked" on
    // a profile that owns no slots sends the player hunting for a rest node
    // that would not help them (#5).
    this.restText.textContent = inv.stash.open
      ? 'REST NODE · stash ' + inv.stash.used + '/' + inv.stash.capacity
      : (inv.atRestNode ? 'REST NODE' : 'in run') + ' · stash locked';
    if (this.listDirty) {
      this.renderList(inv);
      this.listDirty = false;
    }
    if (this.sideDirty) {
      this.renderSide(inv);
      this.sideDirty = false;
    }
    if (this.detailDirty) {
      this.renderDetail(inv);
      this.detailDirty = false;
    }
    if (this.cmpDirty) {
      this.renderCompare(inv);
      this.cmpDirty = false;
    }
  }

  // ------------------------------------------------------------- list

  private renderList(inv: InventoryVM): void {
    // Starring or equipping something forty rows down must not fling you back
    // to the top — that is exactly the inventory friction this panel exists to
    // remove.
    const scroll = this.listHost.scrollTop;
    clear(this.listHost);
    this.visibleIds.length = 0;

    if (this.tab === 'loadouts') {
      this.renderLoadouts(inv);
      this.countText.textContent = '';
      this.listHost.scrollTop = scroll;
      return;
    }
    if (this.tab === 'craft') {
      this.renderCrafting(inv);
      this.countText.textContent =
        inv.crafting.pouchUsed + ' / ' + inv.crafting.pouchCap + ' units carried';
      this.listHost.scrollTop = scroll;
      return;
    }

    const frag = document.createDocumentFragment();
    const held = inv.catalogue ? ' · DEV CATALOGUE, not your holdings' : '';
    if (this.tab === 'weapons') {
      const rows = inv.weapons.filter((w) => this.weaponPasses(w)).sort(byFavouriteThenRarity);
      for (const w of rows) {
        this.visibleIds.push(w.id);
        frag.appendChild(this.weaponRow(w, inv));
      }
      this.countText.textContent = rows.length + ' / ' + inv.weapons.length + ' weapons' + held;
    } else {
      const rows = inv.modules.filter((m) => this.modulePasses(m)).sort(byFavouriteThenRarity);
      for (const m of rows) {
        this.visibleIds.push(m.id);
        frag.appendChild(this.moduleRow(m));
      }
      this.countText.textContent = rows.length + ' / ' + inv.modules.length + ' modules' + held;
    }
    if (this.visibleIds.length === 0) {
      // "Nothing matches those filters" is the wrong sentence when the honest
      // answer is that the run has not found anything yet — a first-run player
      // reading it goes looking for the filter they did not set.
      const empty =
        (this.tab === 'weapons' ? inv.weapons.length : inv.modules.length) === 0
          ? '  nothing here yet — this is what the run is carrying.'
          : '  nothing matches the search.';
      frag.appendChild(el('div', 'detail__line', empty));
    }
    this.listHost.appendChild(frag);
    this.listHost.scrollTop = scroll;
    this.applyCursor();
  }

  private weaponPasses(w: WeaponVM): boolean {
    const f = this.filters;
    if (f.q && !weaponSearchBlob(w).includes(f.q)) return false;
    return true;
  }

  private modulePasses(m: ModuleVM): boolean {
    const f = this.filters;
    if (f.fitOnly) {
      const w = this.focusWeapon();
      if (w && !moduleFitsWeapon(m, w)) return false;
    }
    if (f.q && !moduleSearchBlob(m).includes(f.q)) return false;
    return true;
  }

  private weaponRow(w: WeaponVM, inv: InventoryVM): HTMLElement {
    const row = el('div', 'row');
    // Rows are keyed by instance, not definition: every action this row can
    // fire (equip, stash, focus, install) has to name *this roll* (#12).
    const key = keyOf(w);
    row.dataset.id = key;
    row.setAttribute('role', 'option');
    const equipped = inv.hands.main === key || inv.hands.off === key;
    if (equipped) row.dataset.equipped = '1';
    if (this.focusId === key) row.dataset.focus = '1';

    const star = btn(w.favourite ? '★' : '☆', 'fav-weapon', 'row__star');
    star.dataset.on = w.favourite ? '1' : '0';
    // The old copy promised that starred gear "is never auto-dropped". Nothing
    // auto-drops and there is no drop action, so it described a hazard the
    // player then went looking for.
    star.title = 'Star to pin this to the top of the list.';

    const name = el('span', 'row__name ui-rar', w.name);
    name.dataset.rarity = w.rarity;

    const hand =
      inv.hands.main === key
        ? ' ⟨M⟩'
        : inv.hands.off === key
          ? ' ⟨O⟩'
          : inv.slots.includes(key)
            ? ' ⟨E⟩'
            : '';
    const meta = el('span', 'row__meta', w.className + hand);
    const dmgStat = w.stats[0];
    const stat = el(
      'span',
      'row__stat',
      dmgStat ? formatStatValue(dmgStat.value, dmgStat.format, dmgStat.unit) : '',
    );
    const tags = el(
      'span',
      'row__tags',
      (w.primes.length ? '▲' + w.primes.join('/') + ' ' : '') +
        (w.detonates.length ? '✸' + w.detonates.join('/') + ' ' : '') +
        w.tags.join(' '),
    );

    const acts = el('span', 'row__acts');
    const bm = btn('MAIN', 'equip-main');
    bm.title = 'Equip in the main hand (1 click)';
    const bo = btn('OFF', 'equip-off');
    bo.title = 'Equip in the off hand (1 click)';
    const bc = btn('CMP', 'compare');
    bc.title = 'Add to the side-by-side comparison (up to 3)';
    if (this.compareKind === 'weapon' && this.compare.includes(w.id)) bc.dataset.on = '1';
    const bf = btn('FOCUS', 'focus');
    bf.title = 'Make this the weapon [FIT] installs into, and the synergy subject';
    acts.append(bm, bo, bf, bc);

    row.append(el('span', 'row__glyph', w.glyph), star, name, meta, stat, tags, acts);
    return row;
  }

  private moduleRow(m: ModuleVM): HTMLElement {
    const row = el('div', 'row');
    row.dataset.id = m.id;
    row.setAttribute('role', 'option');
    const focus = this.focusWeapon();
    const fits = focus ? moduleFitsWeapon(m, focus) : false;
    if (!fits) row.dataset.dim = '1';

    const star = btn(m.favourite ? '★' : '☆', 'fav-module', 'row__star');
    star.dataset.on = m.favourite ? '1' : '0';
    star.title = 'Star to pin this to the top of the list.';

    const name = el('span', 'row__name ui-rar', m.name);
    name.dataset.rarity = m.rarity;
    const meta = el('span', 'row__meta', m.family + ' · ' + m.slot);
    const stat = el('span', 'row__stat', m.installedOn ? 'FITTED' : '');
    const tags = el(
      'span',
      'row__tags',
      m.effectText + (m.selfRiskText ? '  ⚠ ' + m.selfRiskText : ''),
    );

    const acts = el('span', 'row__acts');
    if (m.installedOn) {
      const br = btn('PULL', 'remove-module');
      const host = m.installedOn;
      br.dataset.weapon = host;
      br.dataset.slot = m.slot;
      br.dataset.index = String(this.slotIndexOf(host, m.id));
      br.title = 'Remove — the module is returned intact, never consumed.';
      acts.append(br);
    } else {
      const bi = btn('FIT', 'install');
      bi.disabled = !fits;
      bi.title = focus
        ? 'Install into ' + focus.name + ' (1 click)'
        : 'Pick a focus weapon first';
      acts.append(bi);
    }
    const bc = btn('CMP', 'compare');
    bc.title = 'Add to the side-by-side comparison (up to 3)';
    if (this.compareKind === 'module' && this.compare.includes(m.id)) bc.dataset.on = '1';
    acts.append(bc);

    row.append(el('span', 'row__glyph', m.glyph), star, name, meta, stat, tags, acts);
    return row;
  }

  private slotIndexOf(weaponId: string, moduleId: string): number {
    const w = this.inv?.weapons.find((x) => keyOf(x) === weaponId);
    return w?.slots.find((s) => s.moduleId === moduleId)?.index ?? 0;
  }

  /**
   * The rest-node workbench (#8).
   *
   * Two things this list has to keep separate, because they are different
   * sentences: a recipe you have never *read* is absent entirely — the adapter
   * withholds it, since a name you have never seen is not something you can act
   * on — while a recipe you can read and cannot *afford* is right here, with the
   * shortfall on it. Rendering both as "unavailable" would put a checklist where
   * a cost belongs, which is exactly what the carried-pouch decision rejected.
   *
   * The pouch heads the list rather than hiding in a side rail: every row below
   * it is priced in it, and a cost you have to look away from is a cost you
   * cannot compare.
   */
  private renderCrafting(inv: InventoryVM): void {
    const c = inv.crafting;
    const frag = document.createDocumentFragment();

    frag.appendChild(
      el('div', 'col__head', 'Pouch · ' + c.pouchUsed + ' / ' + c.pouchCap + ' units'),
    );
    if (!c.open) frag.appendChild(el('div', 'detail__line ui-warn', '  ' + c.hint));
    if (c.pouch.length === 0) {
      // The cap is the design: carrying salt is choosing not to carry iron.
      frag.appendChild(
        el('div', 'detail__line', '  nothing harvested — dig the world for what recipes cost.'),
      );
    }
    for (const p of c.pouch) {
      const r = el('div', 'slot');
      r.append(el('span', 'slot__kind', String(p.units)));
      r.append(el('span', 'slot__name', p.label));
      frag.appendChild(r);
    }

    // The pickers appear only when something actually asks for them. A pair of
    // dropdowns above a list of recipes that take no materials is furniture.
    if (c.recipes.some((r) => r.needs.includes('fromMaterial'))) {
      const pick = el('div', 'filters');
      pick.append(this.materialPicker('from', this.craftFrom));
      pick.append(this.materialPicker('to', this.craftTo));
      frag.appendChild(pick);
    }

    frag.appendChild(el('div', 'col__head', 'Recipes · ' + c.recipes.length));
    if (c.recipes.length === 0) {
      frag.appendChild(
        el(
          'div',
          'detail__line',
          '  none yet — Salvage grants recipes, and one whose inputs you have never ' +
            'recorded stays hidden until you have.',
        ),
      );
    }
    for (const r of c.recipes) frag.appendChild(this.recipeRow(r, c.open));

    this.listHost.appendChild(frag);
  }

  private materialPicker(which: 'from' | 'to', selected: string): HTMLElement {
    const sel = document.createElement('select');
    sel.dataset.craft = which;
    sel.setAttribute('aria-label', which === 'from' ? 'Convert from' : 'Convert to');
    const blank = document.createElement('option');
    blank.value = '';
    blank.textContent = which === 'from' ? 'convert from…' : 'convert to…';
    sel.append(blank);
    for (const m of this.materials) {
      const o = document.createElement('option');
      o.value = m.id;
      o.textContent = m.label;
      if (m.id === selected) o.selected = true;
      sel.append(o);
    }
    return sel;
  }

  private materialLabel(id: string): string {
    return this.materials.find((m) => m.id === id)?.label ?? id;
  }

  private recipeRow(r: RecipeVM, open: boolean): HTMLElement {
    const row = el('div', 'row');
    row.dataset.id = r.id;

    const head = el('div', 'ui-row');
    head.append(el('span', 'row__name', r.name));
    head.append(el('span', 'chip', r.verb));
    head.append(el('span', 'ui-mute', r.tier));
    row.append(head);
    row.append(el('div', 'row__meta', r.desc));

    // `have` beside `need` on every input, not only the gaps: a recipe you can
    // almost afford and one you cannot begin to are different decisions, and
    // only the full line tells them apart.
    const cost = el('div', 'row__meta');
    for (const i of r.inputs) {
      const span = el('span', i.have >= i.need ? 'ui-good' : 'ui-warn');
      span.textContent = i.label + ' ' + i.have + '/' + i.need + '  ';
      cost.append(span);
    }
    row.append(cost);

    // Recipes taking arguments read them off the panel's live selections, and
    // the row says which — a button that silently consumes whatever happened to
    // be focused is worse than one that names its target first.
    const args = this.craftArgs(r);
    if (r.needs.length > 0) row.append(el('div', 'row__meta ui-mute', args.text));

    const acts = el('div', 'row__acts');
    const go = btn(r.verb.toUpperCase(), 'craft');
    go.dataset.id = r.id;
    go.disabled = !open || !r.craftable || !args.ok;
    go.title = !open
      ? 'Crafting needs a rest node'
      : !r.craftable
        ? r.hint || 'Not enough material in the pouch'
        : args.ok
          ? 'Spend the materials and ' + r.verb + ' it'
          : args.text;
    acts.append(go);
    row.append(acts);

    if (r.hint) row.append(el('div', 'row__meta ui-warn', r.hint));
    return row;
  }

  /**
   * The arguments a recipe needs, taken from what the panel already has
   * selected: the focus weapon, the other hand, the inspected module, and the
   * two material pickers. Nothing here invents a choice the player did not
   * make — if a selection is missing the row says so and the button stays down,
   * because a craft that grafts a module off a weapon you did not mean to strip
   * is not recoverable by pressing undo.
   */
  private craftArgs(r: RecipeVM): { ok: boolean; text: string; args: CraftArgsVM } {
    const inv = this.inv;
    const args: CraftArgsVM = {};
    const parts: string[] = [];
    const missing: string[] = [];

    for (const need of r.needs) {
      switch (need) {
        case 'weapon':
        case 'twoWeapons': {
          const focus = this.focusWeapon();
          if (!focus) {
            missing.push('a focus weapon — press F on one');
            break;
          }
          args.weaponInstanceId = keyOf(focus);
          parts.push('from ' + focus.name);
          if (need !== 'twoWeapons') break;
          const other = inv?.weapons.find((w) => keyOf(w) !== keyOf(focus));
          if (!other) {
            missing.push('a second weapon to graft onto');
            break;
          }
          args.toWeaponInstanceId = keyOf(other);
          parts.push('onto ' + other.name);
          break;
        }
        case 'module': {
          const m = inv?.modules.find((x) => x.id === this.inspectId);
          if (!m) {
            missing.push('a module — click one on the Modules tab');
            break;
          }
          args.moduleId = m.id;
          parts.push('moving ' + m.name);
          break;
        }
        case 'fromMaterial':
        case 'toMaterial': {
          const picked = need === 'fromMaterial' ? this.craftFrom : this.craftTo;
          if (!picked) {
            missing.push(need === 'fromMaterial' ? 'a source material' : 'a target material');
            break;
          }
          args[need] = picked;
          parts.push((need === 'fromMaterial' ? 'from ' : 'to ') + this.materialLabel(picked));
          break;
        }
        default:
          // An argument this panel does not know how to collect. Refuse rather
          // than send a half-filled craft: meta would reject it anyway, and the
          // player would read the refusal as the recipe being broken.
          missing.push(need);
      }
    }

    return missing.length > 0
      ? { ok: false, text: 'needs ' + missing.join(' · '), args }
      : { ok: true, text: parts.join(' · '), args };
  }

  private renderLoadouts(inv: InventoryVM): void {
    const frag = document.createDocumentFragment();
    frag.appendChild(el('div', 'col__head', '6 named loadout slots · one-click restore'));

    for (const l of inv.loadouts) {
      const r = el('div', 'lslot');
      r.append(el('span', 'lslot__idx', String(l.index + 1)));
      const nameIn = document.createElement('input');
      nameIn.value = l.name;
      nameIn.dataset.lname = String(l.index);
      nameIn.setAttribute('aria-label', 'Loadout ' + (l.index + 1) + ' name');
      r.append(nameIn);
      // The summary carries the module count now that a snapshot actually
      // stores one (#17) — docs/04 §7's own example line is "Greatblade + Cryo
      // Sprayer · 7 modules", and until the modules were saved that number
      // would have been a claim the slot could not honour.
      const sum = el('span', 'lslot__sum', l.filled ? l.summary : 'empty');
      if (l.filled && !l.available) {
        sum.append(el('span', 'ui-warn', ' · ' + l.unavailableText));
        // The row is one nowrap line and the warning is at the end of it, so
        // the reason has to survive being clipped. The LOAD button carries it
        // too — a disabled control that cannot say why is the thing being fixed.
        sum.title = l.summary + ' · ' + l.unavailableText;
      }
      r.append(sum);

      const save = btn('SAVE', 'save-loadout');
      save.dataset.index = String(l.index);
      save.title = 'Snapshot the current weapons + modules into this slot (1 click)';
      const load = btn('LOAD', 'load-loadout');
      load.dataset.index = String(l.index);
      // A snapshot naming a weapon the profile no longer holds is refused
      // rather than half-restored: coming back with the same name and a
      // different build is the exact failure this slot exists to prevent.
      load.disabled = !l.filled || !l.available;
      load.title = !l.filled
        ? 'Nothing saved here'
        : l.available
          ? 'Restore this loadout — weapons and modules (1 click)'
          : l.unavailableText;
      const del = btn('✕', 'clear-loadout');
      del.dataset.index = String(l.index);
      del.disabled = !l.filled;
      r.append(save, load, del);
      frag.appendChild(r);
    }

    frag.appendChild(el('div', 'col__head', 'Quick pairs · hold Tab for the radial'));
    for (const p of inv.pairs) {
      const r = el('div', 'lslot');
      r.append(el('span', 'lslot__idx', '◆'));
      r.append(el('span', 'lslot__sum', p.name + ' — ' + pairSummary(p.mainId, p.offId, inv)));
      // A pair that can only be selected and never stored is a hotkey with
      // nothing behind it, which is why both rows read "— + —" forever.
      const set = btn('SET', 'save-pair');
      set.dataset.index = String(p.index);
      set.title = 'Store the weapons you are holding right now as this pair';
      const b = btn(p.active ? 'ACTIVE' : 'SWAP', 'pair');
      b.dataset.index = String(p.index);
      b.disabled = !p.mainId && !p.offId;
      b.title = 'Both saved pairs share one hotkey — press it to alternate.';
      r.append(set, b);
      frag.appendChild(r);
    }
    this.listHost.appendChild(frag);
  }

  // ------------------------------------------------------------- build column

  /**
   * The two hands, their sockets, and everything else the run is carrying.
   *
   * This is the centre column and it renders identically on both tabs, which is
   * what makes the module tab safe: [FIT] installs into the focus weapon, and
   * the focus weapon and its five sockets are on screen while you click it.
   * Previously the target was chosen on one tab and consumed on another, with a
   * trailing clause in a count string as the only reminder.
   */
  private renderSide(inv: InventoryVM): void {
    clear(this.sideHost);
    const focus = this.focusWeapon();
    this.buildHead.textContent = focus ? 'Build · focus: ' + focus.name : 'Build · no focus';
    const frag = document.createDocumentFragment();

    frag.appendChild(el('div', 'col__head', 'Hands'));
    for (const hand of ['main', 'off'] as Hand[]) {
      const id = inv.hands[hand];
      const w = inv.weapons.find((x) => keyOf(x) === id);
      frag.appendChild(this.handBlock(hand, w ?? null, inv, focus));
    }

    // Carried, keyed the way everything else in this panel is. The filter used
    // `w.id` — a *definition* id — against `hands`, which holds instances, so
    // once weapons grew identities (#12) the comparison never matched and both
    // equipped weapons were listed again under "Carried".
    const carried = inv.weapons.filter(
      (w) => keyOf(w) !== inv.hands.main && keyOf(w) !== inv.hands.off,
    );
    frag.appendChild(
      el('div', 'col__head', 'Carried · ' + inv.slots.filter((s) => s).length + ' / 8 slots'),
    );
    if (carried.length === 0) {
      frag.appendChild(el('div', 'detail__line', '  nothing but what you are holding.'));
    }
    for (const w of carried) {
      const r = el('div', 'slot');
      r.dataset.id = keyOf(w);
      const slotIndex = inv.slots.indexOf(keyOf(w));
      r.append(el('span', 'slot__kind', slotIndex >= 0 ? String(slotIndex + 1) : '—'));
      const n = el('span', 'slot__name ui-rar', w.name);
      n.dataset.rarity = w.rarity;
      r.append(n);
      const bm = btn('M', 'equip-main');
      bm.title = 'Equip in the main hand';
      const bo = btn('O', 'equip-off');
      bo.title = 'Equip in the off hand';
      const bf = btn('F', 'focus');
      bf.title = 'Focus: modules fit into this one';
      const bs = btn('↧', 'stash');
      bs.title = inv.stash.open
        ? 'Stow this in the stash — it survives your death'
        : inv.stash.hint;
      bs.disabled = !inv.stash.open;
      r.append(bm, bo, bf, bs);
      frag.appendChild(r);
    }

    frag.appendChild(this.stashBlock(inv));
    this.sideHost.appendChild(frag);
  }

  /**
   * The stash column (#5).
   *
   * It advertised "stash is unlimited" over six permanently-null rows for the
   * whole project, because there was no stash: three Ash unlocks bought a slot
   * count nothing read. What it shows now is the store as it really is — how
   * many slots have been *bought*, what is in them, and, when it is shut, which
   * of the two reasons it is shut for, because "locked" alone is
   * indistinguishable from broken.
   *
   * Rows are definitions rather than rolls, and say so by listing the modules
   * that come back with them. A stashed weapon returns as the same weapon with
   * the same build, not as the same roll, and the panel must not imply more.
   */
  private stashBlock(inv: InventoryVM): DocumentFragment {
    const st = inv.stash;
    const f = document.createDocumentFragment();
    f.append(
      el('div', 'col__head', 'Stash · ' + st.used + ' / ' + st.capacity + ' · survives death'),
    );

    if (!st.open) f.append(el('div', 'detail__line ui-warn', '  ' + st.hint));
    if (st.capacity === 0) return f;
    if (st.items.length === 0) {
      f.append(el('div', 'detail__line', '  empty — ' + st.free + ' slots waiting.'));
      return f;
    }

    for (const it of st.items) {
      const r = el('div', 'slot');
      r.append(el('span', 'slot__kind', it.kind));
      const n = el('span', 'slot__name ui-rar', it.name);
      n.dataset.rarity = it.rarity;
      r.append(el('span', 'row__glyph', it.glyph), n);
      // The build comes back with it, so the build is part of the row. Without
      // this a stashed weapon and a bare one look identical, and the player has
      // to withdraw it to find out which they kept.
      if (it.moduleNames.length > 0) {
        r.append(el('span', 'ui-mute', ' + ' + it.moduleNames.join(', ')));
      }
      const take = btn('TAKE', 'unstash');
      take.dataset.stash = it.id;
      take.disabled = !st.open;
      take.title = st.open ? 'Take it back out — a withdrawal is a move' : st.hint;
      r.append(take);
      f.append(r);
    }
    return f;
  }

  /** One hand: the weapon, its sockets, and the actions that change either. */
  private handBlock(
    hand: Hand,
    w: WeaponVM | null,
    inv: InventoryVM,
    focus: WeaponVM | null,
  ): HTMLElement {
    const box = el('div', 'handbox');
    box.dataset.hand = hand;
    if (w && focus && keyOf(focus) === keyOf(w)) box.dataset.focus = '1';

    const head = el('div', 'slot');
    if (w) head.dataset.id = keyOf(w);
    head.append(el('span', 'slot__kind', hand));
    const n = el('span', 'slot__name ui-rar', w ? w.name : '—');
    if (w) n.dataset.rarity = w.rarity;
    head.append(n);
    if (w) {
      const bf = btn('FOCUS', 'focus');
      bf.title = 'Modules fit into this weapon';
      head.append(bf);
    }
    const un = btn('✕', 'unequip');
    un.dataset.hand = hand;
    un.disabled = !w;
    un.title = 'Empty this hand';
    head.append(un);
    box.append(head);

    if (!w) {
      box.append(el('div', 'detail__line', '  empty — equip something from the list.'));
      return box;
    }

    const byKind = [...w.slots].sort(
      (a, b) => SLOT_ORDER.indexOf(a.kind) - SLOT_ORDER.indexOf(b.kind) || a.index - b.index,
    );
    for (const s of byKind) {
      const m = inv.modules.find((x) => x.id === s.moduleId);
      const r = el('div', 'slot slot--socket');
      if (!m) r.dataset.empty = '1';
      r.append(el('span', 'slot__kind', s.kind));
      const mn = el('span', 'slot__name ui-rar', m ? m.name : 'empty');
      if (m) {
        mn.dataset.rarity = m.rarity;
        r.dataset.id = m.id;
        const rm = btn('✕', 'remove-module');
        rm.dataset.weapon = keyOf(w);
        rm.dataset.slot = s.kind;
        rm.dataset.index = String(s.index);
        rm.title = 'Free respec — the module comes back intact.';
        r.append(mn, rm);
      } else {
        r.append(mn);
      }
      box.append(r);
    }
    return box;
  }

  // ------------------------------------------------------------- detail

  private renderDetail(inv: InventoryVM): void {
    clear(this.detailHost);
    const frag = document.createDocumentFragment();

    const subjectId = this.inspectId ?? this.focusId;
    const w = inv.weapons.find((x) => keyOf(x) === subjectId);
    const m = inv.modules.find((x) => x.id === subjectId);

    if (w) frag.appendChild(this.weaponDetail(w, inv));
    else if (m) frag.appendChild(this.moduleDetail(m, inv));
    else frag.appendChild(el('div', 'detail__line', 'Hover or click anything to inspect it.'));

    this.detailHost.appendChild(frag);
  }

  private weaponDetail(w: WeaponVM, inv: InventoryVM): DocumentFragment {
    const f = document.createDocumentFragment();
    const title = el('div', 'detail__title ui-rar', w.name);
    title.dataset.rarity = w.rarity;
    f.append(title);
    f.append(
      el(
        'div',
        'detail__line',
        w.rarity + ' · ' + w.className + ' · ' + w.archetype + ' · ' + w.damageType,
      ),
    );

    const stats = el('div');
    for (const s of w.stats) {
      const r = el('div', 'detail__line');
      r.append(el('span', 'ui-mute', s.label + ' '));
      r.append(el('span', '', formatStatValue(s.value, s.format, s.unit)));
      stats.append(r);
    }
    f.append(stats);
    f.append(el('div', 'detail__prose', w.effectText));

    f.append(this.synergyBlock(computeSynergy(w, inv)));

    if (w.tags.length) {
      const tagRow = el('div', 'ui-row');
      for (const t of w.tags) tagRow.append(el('span', 'chip', t));
      f.append(tagRow);
    }
    if (w.codexRefs?.length) f.append(this.codexRefs(w.codexRefs));
    return f;
  }

  /** docs/04 §7.1, rendered exactly as specified — with the reason stated. */
  private synergyBlock(s: SynergyHintVM): HTMLElement {
    const box = el('div', 'syn');
    box.append(
      el(
        'div',
        'detail__line',
        s.title +
          '  ·  primes: ' +
          (s.primes.join(', ') || '—') +
          '  ·  detonates: ' +
          (s.detonates.join(', ') || '—'),
      ),
    );

    const g1 = el('div', 'syn__group');
    g1.append(
      el(
        'div',
        'syn__lead',
        s.modulesDetonatingMyPrimers.length + ' owned modules detonate what this primes',
      ),
    );
    if (s.modulesDetonatingMyPrimers.length === 0) {
      g1.append(el('div', 'syn__none', 'none yet — look for modules that consume ' + (s.primes.join('/') || 'a primer')));
    }
    for (const line of s.modulesDetonatingMyPrimers) {
      const d = el('div', 'syn__item');
      d.dataset.id = line.refId;
      d.append(el('b', '', line.subject));
      d.append(el('i', '', ' ' + line.where));
      d.append(document.createTextNode(' — ' + line.reason));
      g1.append(d);
    }
    box.append(g1);

    const g2 = el('div', 'syn__group');
    g2.append(
      el(
        'div',
        'syn__lead',
        s.weaponsPrimingMyDetonations.length + ' owned weapons prime what this detonates',
      ),
    );
    if (s.weaponsPrimingMyDetonations.length === 0) {
      g2.append(el('div', 'syn__none', 'none yet — nothing you own lays down ' + (s.detonates.join('/') || 'a primer it eats')));
    }
    for (const line of s.weaponsPrimingMyDetonations) {
      const d = el('div', 'syn__item');
      d.dataset.id = line.refId;
      d.append(el('b', '', line.subject));
      d.append(el('i', '', ' ' + line.where));
      d.append(document.createTextNode(' — ' + line.reason));
      g2.append(d);
    }
    box.append(g2);

    const g3 = el('div', 'syn__group');
    g3.append(el('div', 'syn__lead', 'Terrain'));
    g3.append(el('div', 'syn__item', s.terrainText));
    box.append(g3);

    for (const wmsg of s.warnings) box.append(el('div', 'syn__warn', wmsg));
    return box;
  }

  private moduleDetail(m: ModuleVM, inv: InventoryVM): DocumentFragment {
    const f = document.createDocumentFragment();
    const title = el('div', 'detail__title ui-rar', m.name);
    title.dataset.rarity = m.rarity;
    f.append(title);
    f.append(
      el('div', 'detail__line', m.rarity + ' · ' + m.family + ' family · ' + m.slot + ' slot'),
    );
    f.append(el('div', 'detail__prose', m.effectText));

    for (const s of m.statMods) {
      const r = el('div', 'detail__line');
      r.append(el('span', 'ui-mute', s.label + ' '));
      r.append(el('span', moduleStatIsGood(s) ? 'ui-good' : 'ui-bad', moduleStatText(s)));
      f.append(r);
    }

    if (m.primes.length || m.detonates.length) {
      f.append(
        el(
          'div',
          'detail__line',
          'primes: ' +
            (m.primes.join(', ') || '—') +
            '  ·  detonates: ' +
            (m.detonates.join(', ') || '—'),
        ),
      );
    }
    if (m.selfRiskText) f.append(el('div', 'syn__warn', m.selfRiskText));

    // Which of your weapons this actually fits — the reverse of §7.1.
    const fits = inv.weapons.filter((w) => moduleFitsWeapon(m, w));
    const box = el('div', 'syn');
    box.append(el('div', 'syn__lead', fits.length + ' owned weapons can take this module'));
    if (fits.length === 0) box.append(el('div', 'syn__none', 'nothing you own has a ' + m.slot + ' slot for it'));
    for (const w of fits.slice(0, 8)) {
      const d = el('div', 'syn__item');
      d.dataset.id = keyOf(w);
      d.append(el('b', '', w.name));
      const shared = m.detonates.filter((x) => w.primes.includes(x));
      d.append(
        document.createTextNode(
          shared.length
            ? ' — detonates the ' + shared.join('/') + ' this weapon primes'
            : ' — ' + w.className + ', ' + m.slot + ' slot free',
        ),
      );
      box.append(d);
    }
    if (fits.length > 8) box.append(el('div', 'syn__none', '…and ' + (fits.length - 8) + ' more'));
    f.append(box);

    if (m.codexRefs?.length) f.append(this.codexRefs(m.codexRefs));
    return f;
  }

  /** docs/04 §7 — every module links to the codex entries it touches. */
  private codexRefs(refs: NonNullable<WeaponVM['codexRefs']>): HTMLElement {
    const row = el('div', 'ui-row');
    row.append(el('span', 'ui-mute', 'codex:'));
    for (const r of refs) {
      const b = btn(r.label, 'codex-ref');
      b.dataset.kind = r.kind;
      b.dataset.ref = r.id;
      row.append(b);
    }
    return row;
  }

  // ------------------------------------------------------------- compare

  private renderCompare(inv: InventoryVM): void {
    clear(this.cmpHost);
    if (this.compare.length === 0) {
      this.cmpHost.hidden = true;
      return;
    }
    this.cmpHost.hidden = false;

    const kind = this.compareKind;
    const head = el('div', 'cmp__head');
    head.append(
      el('span', 'ui-caps ui-dim', 'Comparison · ' + (kind === 'module' ? 'modules' : 'weapons')),
      el('span', 'ui-mute', 'deltas are against column 1'),
      el('span', 'ui-grow'),
      btn('clear', 'compare-clear'),
    );
    this.cmpHost.append(head);

    const scroll = el('div', 'scroll');
    const table = el('table');
    const thead = el('thead');
    const hr = el('tr');
    hr.append(el('th', '', ''));
    const tbody = el('tbody');

    if (kind === 'module') {
      const mods = this.compare
        .map((id) => inv.modules.find((m) => m.id === id))
        .filter((m): m is ModuleVM => !!m);
      mods.forEach((m, i) => {
        const th = el('th', i === 0 ? 'cmp__base' : '', m.name);
        th.title = m.family + ' · ' + m.slot + ' · ' + m.rarity;
        hr.append(th);
      });
      const textRow = (label: string, get: (m: ModuleVM) => string): void => {
        const tr = el('tr');
        tr.append(el('td', 'k', label));
        for (const m of mods) tr.append(el('td', 'v', get(m)));
        tbody.append(tr);
      };
      textRow('family', (m) => m.family);
      textRow('slot', (m) => m.slot);
      textRow('rarity', (m) => m.rarity);
      textRow('primes', (m) => m.primes.join(', ') || '—');
      textRow('detonates', (m) => m.detonates.join(', ') || '—');
      textRow('fits', (m) => (m.compatibleClasses ? m.compatibleClasses.length + ' classes' : 'any'));
      // Where it currently sits: comparing two modules when one of them is
      // already installed is the common case, and swapping is free.
      textRow('installed on', (m) =>
        m.installedOn ? (inv.weapons.find((w) => keyOf(w) === m.installedOn)?.name ?? m.installedOn) : 'loose',
      );
      appendStatRows(tbody, buildModuleComparison(mods));
      // Self-risk last, because it is the argument against the column above it
      // and it should be the last thing read (docs/04 §5.3).
      textRow('self-risk', (m) => m.selfRiskText ?? '—');
      textRow('effect', (m) => m.effectText);
    } else {
      const weapons = this.compare
        .map((id) => inv.weapons.find((w) => keyOf(w) === id))
        .filter((w): w is WeaponVM => !!w);
      weapons.forEach((w, i) => {
        const th = el('th', i === 0 ? 'cmp__base' : '', w.name);
        th.title = w.className + ' · ' + w.rarity;
        hr.append(th);
      });
      const textRow = (label: string, get: (w: WeaponVM) => string): void => {
        const tr = el('tr');
        tr.append(el('td', 'k', label));
        for (const w of weapons) tr.append(el('td', 'v', get(w)));
        tbody.append(tr);
      };
      textRow('class', (w) => w.className);
      textRow('damage type', (w) => w.damageType);
      textRow('rarity', (w) => w.rarity);
      textRow('primes', (w) => w.primes.join(', ') || '—');
      textRow('detonates', (w) => w.detonates.join(', ') || '—');
      appendStatRows(tbody, buildComparison(weapons));
      textRow('terrain', (w) => w.terrainText);
    }

    thead.append(hr);
    table.append(thead, tbody);
    scroll.append(table);
    this.cmpHost.append(scroll);
  }

  // ------------------------------------------------------------- keyboard

  private applyCursor(): void {
    const rows = this.listHost.querySelectorAll<HTMLElement>('.row');
    this.cursor = Math.max(0, Math.min(this.cursor, rows.length - 1));
    rows.forEach((r, i) => {
      const on = i === this.cursor;
      if ((r.dataset.cursor === '1') !== on) r.dataset.cursor = on ? '1' : '0';
      if (on) r.setAttribute('aria-selected', 'true');
      else r.removeAttribute('aria-selected');
    });
  }

  /** Returns true when the key was consumed. UIRoot owns Escape. */
  handleKey(e: KeyboardEvent): boolean {
    const typing =
      document.activeElement instanceof HTMLInputElement ||
      document.activeElement instanceof HTMLTextAreaElement;

    if (e.key === '/' && !typing) {
      this.search.focus();
      this.search.select();
      return true;
    }
    if (typing && e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter') return false;
    if (typing && e.key === 'Enter') {
      this.root.focus();
      return true;
    }

    switch (e.key) {
      case 'ArrowDown':
        this.moveCursor(1);
        return true;
      case 'ArrowUp':
        this.moveCursor(-1);
        return true;
      case 'PageDown':
        this.moveCursor(10);
        return true;
      case 'PageUp':
        this.moveCursor(-10);
        return true;
      case 'Enter': {
        const id = this.visibleIds[this.cursor];
        if (!id) return true;
        if (this.tab === 'weapons') this.cb.onEquip?.(id, e.shiftKey ? 'off' : 'main');
        else if (this.tab === 'modules') this.install(id);
        return true;
      }
      case ' ': {
        const id = this.visibleIds[this.cursor];
        if (id) this.cb.onToggleFavourite?.(this.tab === 'weapons' ? 'weapon' : 'module', id);
        return true;
      }
      case 'c': {
        // Modules compare too now — the guard on `tab === 'weapons'` was the
        // other half of why module comparison did not exist.
        const id = this.visibleIds[this.cursor];
        if (id && this.tab !== 'loadouts') this.toggleCompare(id);
        return true;
      }
      case 'f': {
        const id = this.visibleIds[this.cursor];
        if (id) this.setFocus(id);
        return true;
      }
      case '1':
        this.setTab('weapons');
        return true;
      case '2':
        this.setTab('modules');
        return true;
      case '3':
        this.setTab('loadouts');
        return true;
      case '4':
        this.setTab('craft');
        return true;
    }
    return false;
  }

  private moveCursor(d: number): void {
    const rows = this.listHost.querySelectorAll<HTMLElement>('.row');
    if (rows.length === 0) return;
    this.cursor = Math.max(0, Math.min(this.cursor + d, rows.length - 1));
    this.applyCursor();
    const id = this.visibleIds[this.cursor];
    if (id && this.inspectId !== id) {
      this.inspectId = id;
      this.detailDirty = true;
      this.render();
    }
    rows[this.cursor]?.scrollIntoView({ block: 'nearest' });
  }

  destroy(): void {
    this.root.remove();
  }
}

// ------------------------------------------------------------------ helpers

function kindOfTab(t: Tab): 'weapon' | 'module' {
  return t === 'modules' ? 'module' : 'weapon';
}

/** Stat rows are identical for both kinds once the comparison is built. */
function appendStatRows(tbody: HTMLElement, rows: CompareRowVM[]): void {
  for (const row of rows) {
    const tr = el('tr');
    tr.append(el('td', 'k', row.label));
    for (const c of row.cells) {
      const td = el('td', 'v');
      td.append(document.createTextNode(c.text));
      if (c.deltaText) {
        const d = el('span', 'd', c.deltaText);
        d.dataset.dir = String(c.dir);
        td.append(d);
      }
      tr.append(td);
    }
    tbody.append(tr);
  }
}

function pairSummary(mainId: string | null, offId: string | null, inv: InventoryVM): string {
  const n = (id: string | null): string =>
    id ? (inv.weapons.find((w) => keyOf(w) === id)?.name ?? '?') : '—';
  return n(mainId) + ' + ' + n(offId);
}
